import React, { useState } from 'react'
import {
  Modal,
  Button,
  Icon,
  Alert,
  Upload,
  Tabs
} from 'antd'
import './VoiceSelectionModal.scss'
import VoiceUploadDetailsModal from './VoiceUploadDetailsModal'

const { TabPane } = Tabs

const getVoicePreviewUrl = voice =>
  voice?.audio_url || voice?.audioUrl || voice?.preview_url || ''

const formatMetaValue = value => {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'string' && value.trim() === '') return 'N/A'
  return value
}

const getVoiceMetaItems = voice => {
  const rawItems = [
    { label: 'Language', value: voice.language },
    { label: 'Accent', value: voice.accent },
    { label: 'Gender', value: voice.gender },
    { label: 'Age Range', value: voice.age_range }
  ]

  return rawItems
    .map(item => ({ ...item, value: formatMetaValue(item.value) }))
    .filter(item => item.value !== 'N/A')
}

const formatVoiceLabel = (variant, index) => {
  const numericLabel = String(index + 1).padStart(2, '0')

  if (variant === 'recommended') return `Preferred Voice ${numericLabel}`
  if (variant === 'custom') return `Custom Voice ${numericLabel}`
  return `Default Voice ${numericLabel}`
}

const getVoiceDescription = (voice, variant) => {
  if (variant === 'recommended') {
    return voice.reasoning || voice.description || 'Recommended for your story.'
  }

  if (variant === 'custom') {
    return (
      voice.description ||
      'Your uploaded voice, ready to use anywhere this narration style fits.'
    )
  }

  return voice.description || 'A platform voice from the built-in library.'
}

const getSourceIcon = variant => {
  if (variant === 'recommended') return 'star'
  if (variant === 'custom') return 'user'
  return 'customer-service'
}

const getVoiceSubtitle = (voice, variant) => {
  if (variant === 'recommended' && typeof voice.rank === 'number') {
    return `Recommended Voice • Rank ${voice.rank}`
  }

  if (variant === 'custom') {
    return 'Custom Voice'
  }

  return 'System Voice'
}

const VoiceDetailsModal = ({ voice, variant, displayName, visible, onClose }) => {
  if (!voice) return null

  const metaItems = getVoiceMetaItems(voice)
  const originalName = formatMetaValue(voice.name)
  const description = getVoiceDescription(voice, variant)
  const sourceLabel =
    variant === 'recommended'
      ? 'Recommended'
      : variant === 'custom'
      ? 'Custom Upload'
      : 'System Library'

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      centered
      className="voice-selection-modal__details-modal"
      title={displayName}
    >
      <div className="voice-selection-modal__details-source">{sourceLabel}</div>

      <div className="voice-selection-modal__details-list">
        <div className="voice-selection-modal__details-row">
          <span className="voice-selection-modal__details-key">
            Original name
          </span>
          <span className="voice-selection-modal__details-value">
            {originalName}
          </span>
        </div>

        {metaItems.map(item => (
          <div
            className="voice-selection-modal__details-row"
            key={item.label}
          >
            <span className="voice-selection-modal__details-key">
              {item.label}
            </span>
            <span className="voice-selection-modal__details-value">
              {item.value}
            </span>
          </div>
        ))}

        {variant === 'recommended' && typeof voice.rank === 'number' && (
          <div className="voice-selection-modal__details-row">
            <span className="voice-selection-modal__details-key">Rank</span>
            <span className="voice-selection-modal__details-value">
              {voice.rank}
            </span>
          </div>
        )}
      </div>

      {!!description && (
        <div className="voice-selection-modal__details-description">
          {description}
        </div>
      )}
    </Modal>
  )
}

const VoiceListItem = ({
  voice,
  variant,
  displayName,
  isSelected,
  isPlaying,
  progress,
  onSelect,
  onPlay,
  onViewDetails
}) => {
  const sourceIcon = getSourceIcon(variant)
  const subtitle = getVoiceSubtitle(voice, variant)

  return (
    <div
      className={`voiceover-card ${isSelected ? 'voiceover-card--selected' : ''}`}
      onClick={onSelect}
    >
      <div className="voiceover-card__identity">
        <div className={`voiceover-card__source voiceover-card__source--${variant}`}>
          <Icon type={sourceIcon} />
        </div>
      </div>

      <div className="voiceover-card__content">
        <div className="voiceover-card__title-row">
          <div className="voiceover-card__title-block">
            <span className="voiceover-card__name">{displayName}</span>
          </div>
        </div>

        <span className="voiceover-card__subtitle">{subtitle}</span>
      </div>

      <div className="voiceover-card__actions">
        <button
          className="voiceover-card__icon-btn"
          onClick={e => {
            e.stopPropagation()
            onViewDetails()
          }}
          type="button"
          aria-label={`View details for ${displayName}`}
        >
          <Icon type="eye" />
        </button>
        <div
          className={`voiceover-card__play-wrapper ${
            isPlaying ? 'voiceover-card__play-wrapper--playing' : ''
          }`}
          style={{
            position: 'relative',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            overflow: 'hidden'
          }}
        >
          {isPlaying && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `conic-gradient(#5057d5 ${progress}%, #eef0ff 0)`,
                zIndex: 0
              }}
            />
          )}
          <button
            className={`voiceover-card__play-btn ${
              isPlaying ? 'voiceover-card__play-btn--playing' : ''
            }`}
            onClick={e => {
              e.stopPropagation()
              onPlay()
            }}
            style={
              isPlaying
                ? {
                    position: 'absolute',
                    top: '3px',
                    left: '3px',
                    right: '3px',
                    bottom: '3px',
                    width: 'calc(100% - 6px)',
                    height: 'calc(100% - 6px)',
                    zIndex: 1,
                    borderRadius: '50%',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }
                : {}
            }
            type="button"
          >
            <Icon
              type={isPlaying ? 'pause' : 'caret-right'}
              style={isPlaying ? { color: '#5057d5', fontSize: '14px' } : {}}
            />
          </button>
        </div>

        <button
          className={`voiceover-card__select-btn ${
            isSelected ? 'voiceover-card__select-btn--selected' : ''
          }`}
          onClick={e => {
            e.stopPropagation()
            onSelect()
          }}
          type="button"
        >
          {isSelected && <Icon type="check" />}
          <span>{isSelected ? 'Selected' : 'Select'}</span>
        </button>
      </div>
    </div>
  )
}

const VoiceLibrarySection = ({
  title,
  description,
  onRefresh,
  isRefreshing,
  error,
  isLoading,
  loadingText,
  emptyIcon,
  emptyText,
  children
}) => (
  <div className="voice-selection-modal__library-section">
    <div className="voice-selection-modal__header">
      <div className="voice-selection-modal__section-copy">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <Button
        type="default"
        icon="reload"
        onClick={onRefresh}
        loading={isRefreshing}
        className="voice-selection-modal__refresh-btn"
      >
        Refresh
      </Button>
    </div>

    {error && (
      <Alert
        message={error}
        type="warning"
        showIcon
        className="voice-selection-modal__alert"
      />
    )}

    {isLoading ? (
      <div className="voice-selection-modal__state-card">
        <div className="processing-visual">
          <div className="custom-loader" />
          <div className="loader-center">
            <div className="inner-dot" />
          </div>
        </div>
        <p>{loadingText}</p>
      </div>
    ) : children ? (
      children
    ) : (
      <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
        <Icon type={emptyIcon} />
        <p>{emptyText}</p>
      </div>
    )}
  </div>
)

const VoiceSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  voices,
  loadingVoices,
  handlePlayPreview,
  playingVoice,
  audioProgress,
  selectedVoiceId,
  targetName,
  handleUploadVoice,
  uploading,
  uploadProgress,
  uploadError,
  setUploadError,
  beforeUpload,
  fetchVoices,
  pendingUploadFile,
  setPendingUploadFile,
  pendingVoiceName,
  pendingVoiceDescription,
  pendingVoiceLanguage,
  setPendingVoiceName,
  setPendingVoiceDescription,
  setPendingVoiceLanguage,
  onRetryUpload,
  onOverwriteUpload,
  recommendedVoices = [],
  loadingRecommendedVoices = false,
  recommendedVoicesError = null,
  voicesError = null,
  showRecommendedTab = false,
  fetchRecommendedVoices
}) => {
  const [activeTab, setActiveTab] = useState(
    showRecommendedTab ? 'recommended' : 'system'
  )
  const [detailsView, setDetailsView] = useState(null)
  const [isUploadDetailsModalVisible, setUploadDetailsModalVisible] = useState(
    false
  )
  const resolvedTargetName =
    typeof targetName === 'object'
      ? targetName?.name
      : typeof targetName === 'string'
      ? targetName.trim()
      : ''
  const modalTargetName =
    resolvedTargetName || (showRecommendedTab ? 'Narrator' : '')

  const systemVoices = voices.filter(v => v.type !== 'uploaded')
  const customVoices = voices.filter(v => v.type === 'uploaded')
  const hasSystemVoices = systemVoices.length > 0
  const hasCustomVoices = customVoices.length > 0

  const openUploadDetails = file => {
    if (setPendingUploadFile) {
      setPendingUploadFile(file)
    }
    if (!pendingVoiceName?.trim() && file) {
      setPendingVoiceName(file.name.replace(/\.[^/.]+$/, ''))
    }
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
    setUploadDetailsModalVisible(true)
  }

  const closeUploadDetails = () => {
    setUploadDetailsModalVisible(false)
    if (setPendingUploadFile) {
      setPendingUploadFile(null)
    }
    setPendingVoiceName('')
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
  }

  const handleFileSelect = file => {
    const fileAllowed = beforeUpload ? beforeUpload(file) : true
    if (fileAllowed !== false) {
      openUploadDetails(file)
    }
    return false
  }

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile) {
      return
    }
    await handleUploadVoice({
      file: pendingUploadFile,
      onSuccess: () => {
        closeUploadDetails()
      },
      onError: () => {}
    })
  }

  const handleRetryUploadWithClose = async () => {
    if (typeof onRetryUpload !== 'function') return
    await onRetryUpload({
      onSuccess: () => {
        closeUploadDetails()
      }
    })
  }

  const handleOverwriteUploadWithClose = async () => {
    if (typeof onOverwriteUpload !== 'function') return
    await onOverwriteUpload({
      onSuccess: () => {
        closeUploadDetails()
      }
    })
  }

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={
        modalTargetName ? `Select Voice for ${modalTargetName}` : 'Select Voice'
      }
      centered
      className="voice-selection-modal"
      destroyOnClose
    >
      <div className="voice-selection-modal__content">
        <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false}>
          {showRecommendedTab && (
            <TabPane
              tab={
                <span>
                  <Icon type="star" />
                  Recommended Voices
                </span>
              }
              key="recommended"
            >
              <div className="voice-selection-modal__tab-content">
                <div className="voice-selection-modal__recommendation-hero">
                  <div className="voice-selection-modal__recommendation-copy">
                    <h3>Recommended for your story</h3>
                    <p>
                      Start with the top match, compare a few options, and use
                      the eye icon to inspect each voice before choosing.
                    </p>
                  </div>
                  {typeof fetchRecommendedVoices === 'function' && (
                    <Button
                      type="default"
                      icon="reload"
                      onClick={fetchRecommendedVoices}
                      loading={loadingRecommendedVoices}
                      className="voice-selection-modal__refresh-btn"
                    >
                      Refresh
                    </Button>
                  )}
                </div>

                {recommendedVoicesError && (
                  <Alert
                    message={recommendedVoicesError}
                    type="warning"
                    showIcon
                    className="voice-selection-modal__alert"
                  />
                )}

                {loadingRecommendedVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Curating recommended voices...</p>
                  </div>
                ) : recommendedVoices.length > 0 ? (
                  <div className="voice-selection-modal__voice-list">
                    {recommendedVoices.map((voice, index) => (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        variant="recommended"
                        displayName={formatVoiceLabel('recommended', index)}
                        isSelected={selectedVoiceId === voice.id}
                        isPlaying={playingVoice === voice.id}
                        progress={playingVoice === voice.id ? audioProgress : 0}
                        onSelect={() => onSelect(voice.id)}
                        onViewDetails={() =>
                          setDetailsView({
                            voice,
                            variant: 'recommended',
                            displayName: formatVoiceLabel('recommended', index)
                          })
                        }
                        onPlay={() =>
                          handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="bulb" />
                    <p>No recommendations available for this story yet.</p>
                  </div>
                )}
              </div>
            </TabPane>
          )}

          <TabPane
            tab={
              <span>
                <Icon type="customer-service" />
                System Voices
              </span>
            }
            key="system"
          >
            <div className="voice-selection-modal__tab-content">
              <VoiceLibrarySection
                title="System voices"
                description="Choose from our library of high-quality AI voices with a cleaner, easier comparison layout."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={loadingVoices}
                loadingText="Loading available system voices..."
                emptyIcon="customer-service"
                emptyText="No system voices are available right now."
              >
                {hasSystemVoices && (
                  <div className="voice-selection-modal__voice-list">
                    {systemVoices.map((voice, index) => (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        variant="system"
                        displayName={formatVoiceLabel('system', index)}
                        isSelected={selectedVoiceId === voice.id}
                        isPlaying={playingVoice === voice.id}
                        progress={playingVoice === voice.id ? audioProgress : 0}
                        onSelect={() => onSelect(voice.id)}
                        onViewDetails={() =>
                          setDetailsView({
                            voice,
                            variant: 'system',
                            displayName: formatVoiceLabel('system', index)
                          })
                        }
                        onPlay={() =>
                          handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                        }
                      />
                    ))}
                  </div>
                )}
              </VoiceLibrarySection>
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <Icon type="user" />
                Custom Voices
              </span>
            }
            key="custom"
          >
            <div className="voice-selection-modal__tab-content">
              <VoiceLibrarySection
                title="Custom voices"
                description="Upload your own sample, then compare saved custom voices using the same layout as the library."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={false}
                emptyIcon="user"
                emptyText="No custom voices uploaded yet."
              >
                <div className="voice-selection-modal__upload-section">
                  {uploadError && !isUploadDetailsModalVisible && (
                    <Alert
                      message={uploadError}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setUploadError(null)}
                      className="voice-selection-modal__alert"
                    />
                  )}

                  <Upload.Dragger
                    accept=".mp3"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to start uploading a custom
                      voice.
                    </p>
                    <p className="ant-upload-hint">
                      Upload an MP3 file first, then add name, description, and
                      an optional language.
                    </p>
                  </Upload.Dragger>

                  <VoiceUploadDetailsModal
                    visible={isUploadDetailsModalVisible}
                    pendingUploadFile={pendingUploadFile}
                    uploadError={uploadError}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    pendingVoiceName={pendingVoiceName}
                    pendingVoiceDescription={pendingVoiceDescription}
                    pendingVoiceLanguage={pendingVoiceLanguage}
                    setPendingVoiceName={setPendingVoiceName}
                    setPendingVoiceDescription={setPendingVoiceDescription}
                    setPendingVoiceLanguage={setPendingVoiceLanguage}
                    onCancel={closeUploadDetails}
                    onConfirmUpload={handleConfirmUpload}
                    onRetryUpload={handleRetryUploadWithClose}
                    onOverwriteUpload={handleOverwriteUploadWithClose}
                  />
                </div>

                {loadingVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Loading your saved custom voices...</p>
                  </div>
                ) : hasCustomVoices ? (
                  <div className="voice-selection-modal__voice-list voice-selection-modal__voice-grid">
                    {customVoices.map((voice, index) => (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        variant="custom"
                        displayName={formatVoiceLabel('custom', index)}
                        isSelected={selectedVoiceId === voice.id}
                        isPlaying={playingVoice === voice.id}
                        progress={playingVoice === voice.id ? audioProgress : 0}
                        onSelect={() => onSelect(voice.id)}
                        onViewDetails={() =>
                          setDetailsView({
                            voice,
                            variant: 'custom',
                            displayName: formatVoiceLabel('custom', index)
                          })
                        }
                        onPlay={() =>
                          handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="user" />
                    <p>No custom voices uploaded yet.</p>
                  </div>
                )}
              </VoiceLibrarySection>
            </div>
          </TabPane>
        </Tabs>
      </div>

      <VoiceDetailsModal
        visible={!!detailsView}
        voice={detailsView?.voice}
        variant={detailsView?.variant}
        displayName={detailsView?.displayName}
        onClose={() => setDetailsView(null)}
      />
    </Modal>
  )
}

export default VoiceSelectionModal
